import { createSlice } from '@reduxjs/toolkit';
import { clearState } from './clearSlice';

const initialState = {
   isActionPending: false,
   userListSelection: {},
   channelInfo: {}, // Store channel info by channelId
};

function deepMerge(target, source) {
   const output = { ...target };

   for (const key in source) {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (
         sourceValue &&
         typeof sourceValue === 'object' &&
         !Array.isArray(sourceValue) &&
         sourceValue.constructor === Object
      ) {
         output[key] = deepMerge(targetValue && typeof targetValue === 'object' ? targetValue : {}, sourceValue);
      } else {
         output[key] = sourceValue;
      }
   }

   return output;
}

const channelInfoSlice = createSlice({
   name: 'channelInfoData',
   initialState,
   reducers: {
      selectParticipant(state, action) {
         const { userListScopeKey, userId } = action.payload;
         state.userListSelection[userListScopeKey] = [...(state.userListSelection[userListScopeKey] || []), { userId }];
      },
      removeParticipant(state, action) {
         const { userListScopeKey, userId } = action.payload;
         state.userListSelection[userListScopeKey] = (state.userListSelection[userListScopeKey] || []).filter(
            participant => participant.userId !== userId,
         );
      },
      clearChannelPartcipants(state, action) {
         const { userListScopeKey } = action.payload;
         state.userListSelection[userListScopeKey] = [];
      },
      // New action to store channel info
      setChannelInfo(state, action) {
         const { channelId } = action.payload;
         state.channelInfo[channelId] = {
            ...state.channelInfo[channelId],
            memberCount: 2,
            ...action.payload,
         };
      },
      // Action to store multiple channels info
      setMultipleChannelsInfo(state, action) {
         const channels = action.payload;
         channels.forEach(channel => {
            const existing = state.channelInfo[channel.channelId] || {};
            const merged = deepMerge(existing, channel);
            state.channelInfo[channel.channelId] = merged;
         });
      },
      setChannelParticipants(state, action) {
         const { members, channelId } = action.payload;

         if (!state.channelInfo[channelId]) {
            state.channelInfo[channelId] = {};
         }

         state.channelInfo[channelId].participants = members;
      },
      addChannelMember(state, action) {
         const { channel, participants = [] } = action.payload;
         const channelId = channel.channelId;

         if (!state.channelInfo[channelId]) {
            state.channelInfo[channelId] = {
               ...channel,
               memberCount: participants.length,
               participants: participants.map(p => ({
                  userId: p.userId || p.user_id,
                  roleId: p.roleId || p.role_id,
               })),
            };
         } else {
            const existingParticipants = state.channelInfo[channelId].participants || [];
            const combinedMap = new Map();

            // Merge existing participants
            existingParticipants.forEach(p => {
               const userId = p.userId || p.user_id;
               combinedMap.set(userId, {
                  userId,
                  roleId: p.roleId || p.role_id,
               });
            });

            // Add new participants
            participants.forEach(p => {
               const userId = p.userId || p.user_id;
               combinedMap.set(userId, {
                  userId,
                  roleId: p.roleId || p.role_id,
               });
            });

            const combinedParticipants = Array.from(combinedMap.values());

            state.channelInfo[channelId] = {
               ...state.channelInfo[channelId],
               ...channel,
               memberCount: combinedParticipants.length,
               participants: combinedParticipants,
            };
         }
      },
      leaveMemberFromChannel(state, action) {
         const { channel, member } = action.payload;
         const channelId = channel?.channelId;
         const userId = member?.userId;
         const channelData = state.channelInfo?.[channelId];
         if (!channelData) return;
         channelData.member.userId === userId && (channelData.member = { ...channelData.member, status: 0 });
         if (Object.hasOwn(channelData, 'isMember') && channelData.member.userId === userId) {
            channelData.isMember = 0;
         }
      },
      deleteChannel(state, action) {
         const { channelId } = action.payload;
         if (state.channelInfo[channelId]) {
            delete state.channelInfo[channelId];
         }
      },
      updateChannelStatus(state, action) {
         const { channelId, status } = action.payload;
         if (state.channelInfo[channelId]) {
            state.channelInfo[channelId].status = status;
         }
      },

      clearChannelInfoPartcipants(state, action) {
         const { channelId } = action.payload;
         state.channelInfo[channelId].participants = [];
      },

      toggleCreatingChannel(state) {
         state.isActionPending = !state.isActionPending;
      },
      updateRoleId(state, action) {
         const { channelId, userId, roleId } = action.payload;
         const channel = state.channelInfo[channelId];

         if (channel.member?.userId === userId) {
            channel.member.roleId = roleId;
         }
      },
      setActionStatus(state, action) {
         const { channelIds = [], ...rest } = action.payload;
         channelIds.forEach(channelId => {
            state.channelInfo[channelId] = {
               ...state.channelInfo[channelId],
               ...rest,
               channelId,
            };
         });
      },
      setPinnedStatus(state, action) {
         const { chats = [], action: pinAction = 0 } = action.payload;
         chats.forEach(chat => {
            const { channelId, pinTime = 0 } = chat;
            const pinnedAt = pinAction === 1 ? pinTime : 0;
            if (state.channelInfo[channelId]) {
               state.channelInfo[channelId].pinnedAt = pinnedAt;
            }
         });
      },
      setChannelUnreadCount(state, action) {
         const { channelId, unreadCount, messageId } = action.payload;
         if (state.channelInfo[channelId]) {
            state.channelInfo[channelId].unreadCount = unreadCount;

            // If messageId is empty string, set sequenceId to 0
            const lastReadMessageUpdate = messageId === '' ? { messageId, sequenceId: 0 } : { messageId };

            if (state.channelInfo[channelId].lastReadMessage) {
               state.channelInfo[channelId].lastReadMessage = {
                  ...state.channelInfo[channelId].lastReadMessage,
                  ...lastReadMessageUpdate,
               };
            } else {
               state.channelInfo[channelId].lastReadMessage = lastReadMessageUpdate;
            }
         }
      },
      // Overwrite the channel's lastRecentMessage with a provided recent message
      overwriteChannelLastReadMessage(state, action) {
         const { channelId, lastRecentMessage } = action.payload || {};
         
         if (!channelId) return;

         if (!state.channelInfo[channelId]) {
            state.channelInfo[channelId] = {};
         }

         // Directly overwrite lastRecentMessage with the provided recent message object
         state.channelInfo[channelId].lastRecentMessage = lastRecentMessage;
      },
      decrementChannelUnreadCount(state, action) {
         const { channelId, decrementBy = 1 } = action.payload;
         if (state.channelInfo[channelId]) {
            const currentCount = state.channelInfo[channelId].unreadCount || 0;
            // Ensure count doesn't go below 0
            state.channelInfo[channelId].unreadCount = Math.max(0, currentCount - decrementBy);
         }
      },
   },
   extraReducers: builder => {
      builder.addCase(clearState, () => initialState);
   },
});

export const {
   selectParticipant,
   removeParticipant,
   clearChannelPartcipants,
   setChannelInfo,
   setMultipleChannelsInfo,
   toggleCreatingChannel,
   setChannelParticipants,
   addChannelMember,
   leaveMemberFromChannel,
   deleteChannel,
   clearChannelInfoPartcipants,
   updateChannelStatus,
   updateRoleId,
   setActionStatus,
   setPinnedStatus,
   setChannelUnreadCount,
   decrementChannelUnreadCount,
   overwriteChannelLastReadMessage,
} = channelInfoSlice.actions;
export default channelInfoSlice.reducer;
